import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Bug, 
  Download, 
  Trash2, 
  Filter, 
  Search, 
  ChevronDown, 
  ChevronUp,
  Activity,
  Database,
  Clock,
  AlertTriangle,
  Info,
  X
} from 'lucide-react'
import logger, { LogLevel, LogEntry } from '../../utils/logger'

interface DebugPanelProps {
  isOpen: boolean
  onClose: () => void
}

const DebugPanel: React.FC<DebugPanelProps> = ({ isOpen, onClose }) => {
  const [logs, setLogs] = useState<LogEntry[]>([])
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedLevel, setSelectedLevel] = useState<LogLevel>(LogLevel.DEBUG)
  const [searchTerm, setSearchTerm] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [expandedLog, setExpandedLog] = useState<string | null>(null)

  // Refresh logs
  const refreshLogs = () => {
    const allLogs = logger.getLogs()
    setLogs(allLogs)
  }

  // Auto-refresh logs
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(refreshLogs, 1000)
    return () => clearInterval(interval)
  }, [autoRefresh])

  // Initial load
  useEffect(() => {
    refreshLogs()
  }, [])

  // Filter logs
  useEffect(() => {
    let filtered = logs

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(log => log.category === selectedCategory)
    }

    // Filter by level
    filtered = filtered.filter(log => log.level >= selectedLevel)

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(term) ||
        log.category.toLowerCase().includes(term) ||
        (log.component && log.component.toLowerCase().includes(term))
      )
    }

    setFilteredLogs(filtered.slice(-100)) // Show only last 100 logs
  }, [logs, selectedCategory, selectedLevel, searchTerm])

  const getLevelIcon = (level: LogLevel) => {
    switch (level) {
      case LogLevel.DEBUG:
        return <Bug className="w-4 h-4 text-ink-400" />
      case LogLevel.INFO:
        return <Info className="w-4 h-4 text-terra-500" />
      case LogLevel.WARN:
        return <AlertTriangle className="w-4 h-4 text-ochre-500" />
      case LogLevel.ERROR:
      case LogLevel.CRITICAL:
        return <AlertTriangle className="w-4 h-4 text-blood-500" />
      default:
        return <Info className="w-4 h-4 text-ink-400" />
    }
  }

  const getLevelColor = (level: LogLevel) => {
    switch (level) {
      case LogLevel.DEBUG:
        return 'text-ink-500 dark:text-ink-300'
      case LogLevel.INFO:
        return 'text-terra-500 dark:text-terra-300'
      case LogLevel.WARN:
        return 'text-ochre-500 dark:text-ochre-500'
      case LogLevel.ERROR:
      case LogLevel.CRITICAL:
        return 'text-blood-500 dark:text-blood-500'
      default:
        return 'text-ink-500 dark:text-ink-300'
    }
  }

  const exportLogs = () => {
    const logsData = logger.exportLogs()
    const blob = new Blob([logsData], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `bulk-generation-logs-${new Date().toISOString().split('T')[0]}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const clearLogs = () => {
    logger.clearLogs()
    refreshLogs()
  }

  const stats = logger.getStats()
  const categories = ['all', ...Object.keys(stats.logsByCategory)]

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-ink-900 bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-surface dark:bg-ink-700 rounded-caderno-sm shadow-caderno-xl w-full max-w-6xl h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-cream-400 dark:border-ink-500">
            <div className="flex items-center space-x-3">
              <Bug className="w-6 h-6 text-terra-500" />
              <h2 className="text-xl font-semibold text-ink-900 dark:text-cream-50">
                Debug Panel
              </h2>
              <div className="flex items-center space-x-4 text-sm text-ink-400 dark:text-ink-300">
                <span>Total: {stats.totalLogs}</span>
                <span>Session: {stats.sessionId.slice(-8)}</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`px-3 py-1 rounded-caderno-xs text-sm ${
                  autoRefresh 
                    ? 'bg-moss-100 dark:bg-moss-500/30 text-moss-500 dark:bg-moss-500/30 dark:text-moss-100' 
                    : 'bg-cream-100 text-ink-700 dark:bg-ink-700 dark:text-ink-300'
                }`}
              >
                <Activity className="w-4 h-4 inline mr-1" />
                {autoRefresh ? 'Live' : 'Paused'}
              </button>
              
              <button
                onClick={exportLogs}
                className="p-2 text-ink-400 hover:text-ink-700 dark:text-ink-400 dark:hover:text-cream-200"
                title="Export logs"
              >
                <Download className="w-4 h-4" />
              </button>
              
              <button
                onClick={clearLogs}
                className="p-2 text-ink-400 hover:text-blood-500 dark:text-ink-400 dark:hover:text-blood-500"
                title="Clear logs"
              >
                <Trash2 className="w-4 h-4" />
              </button>
              
              <button
                onClick={onClose}
                className="p-2 text-ink-400 hover:text-ink-700 dark:text-ink-400 dark:hover:text-cream-200"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center space-x-4 p-4 border-b border-cream-400 dark:border-ink-500 bg-cream-50 dark:bg-ink-700/50">
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-ink-400" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-1 border border-cream-400 dark:border-ink-500 rounded-caderno-xs text-sm bg-surface dark:bg-ink-700 text-ink-900 dark:text-cream-50"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(Number(e.target.value) as LogLevel)}
                className="px-3 py-1 border border-cream-400 dark:border-ink-500 rounded-caderno-xs text-sm bg-surface dark:bg-ink-700 text-ink-900 dark:text-cream-50"
              >
                <option value={LogLevel.DEBUG}>Debug+</option>
                <option value={LogLevel.INFO}>Info+</option>
                <option value={LogLevel.WARN}>Warn+</option>
                <option value={LogLevel.ERROR}>Error+</option>
              </select>
            </div>

            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-ink-400" />
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-1 border border-cream-400 dark:border-ink-500 rounded-caderno-xs text-sm bg-surface dark:bg-ink-700 text-ink-900 dark:text-cream-50 placeholder-ink-300"
              />
            </div>
          </div>

          {/* Logs */}
          <div className="flex-1 overflow-auto p-4 space-y-2">
            {filteredLogs.length === 0 ? (
              <div className="text-center text-ink-400 dark:text-ink-300 py-8">
                No logs match the current filters
              </div>
            ) : (
              filteredLogs.map((log, index) => (
                <div
                  key={`${log.timestamp}-${index}`}
                  className="border border-cream-400 dark:border-ink-500 rounded-caderno-sm p-3 bg-surface dark:bg-ink-700"
                >
                  <div 
                    className="flex items-start justify-between cursor-pointer"
                    onClick={() => setExpandedLog(expandedLog === log.timestamp ? null : log.timestamp)}
                  >
                    <div className="flex items-start space-x-3 flex-1">
                      {getLevelIcon(log.level)}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 text-sm">
                          <span className={`font-medium ${getLevelColor(log.level)}`}>
                            {LogLevel[log.level]}
                          </span>
                          <span className="text-ink-400 dark:text-ink-300">
                            [{log.category}]
                          </span>
                          {log.component && (
                            <span className="text-terra-500 dark:text-terra-300">
                              {log.component}
                            </span>
                          )}
                          <span className="text-xs text-ink-400">
                            <Clock className="w-3 h-3 inline mr-1" />
                            {new Date(log.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        
                        <p className="text-ink-900 dark:text-cream-50 mt-1">
                          {log.message}
                        </p>
                      </div>
                    </div>
                    
                    <button className="text-ink-400 hover:text-ink-700 dark:hover:text-cream-200">
                      {expandedLog === log.timestamp ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  {expandedLog === log.timestamp && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="mt-3 pt-3 border-t border-cream-400 dark:border-ink-500"
                    >
                      {log.data && (
                        <div className="mb-2">
                          <h4 className="text-sm font-medium text-ink-700 dark:text-cream-300 mb-1">
                            Data:
                          </h4>
                          <pre className="text-xs bg-cream-100 dark:bg-ink-700 p-2 rounded-caderno-xs overflow-x-auto">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </div>
                      )}
                      
                      {log.error && (
                        <div className="mb-2">
                          <h4 className="text-sm font-medium text-blood-500 dark:text-blood-100 mb-1">
                            Error:
                          </h4>
                          <pre className="text-xs bg-blood-100 dark:bg-blood-500/20 dark:bg-blood-500/20 p-2 rounded-caderno-xs overflow-x-auto text-blood-500 dark:text-blood-100">
                            {log.error.stack || log.error.message}
                          </pre>
                        </div>
                      )}
                      
                      <div className="text-xs text-ink-400 dark:text-ink-300 space-y-1">
                        <div>Session: {log.sessionId}</div>
                        {log.userId && <div>User: {log.userId}</div>}
                        {log.duration && <div>Duration: {log.duration.toFixed(2)}ms</div>}
                      </div>
                    </motion.div>
                  )}
                </div>
              ))
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default DebugPanel
